import { useEffect, useState } from 'preact/hooks';
import { Link } from 'wouter-preact';
import { Check, ChevronRight } from 'lucide-preact';
import { api } from '@/lib/teaching/teacher-api';
import type { Checklist } from '@/lib/teaching/slice6';

// "Get set up" on Home (slice 6 §07, W14). The ticks come from the server,
// which reads them from what the teacher has really done. It stays until
// every step is done or the teacher hides it.

export function SetupChecklist() {
  const [c, setC] = useState<Checklist | null>(null);
  useEffect(() => { void api<Checklist>('GET', '/api/t/setup-checklist').then(setC).catch(() => setC(null)); }, []);
  if (!c || !c.show) return null;
  const n = c.steps.filter((s) => s.done).length;
  const next = c.steps.find((s) => !s.done);
  const hide = async () => { setC(await api<Checklist>('POST', '/api/t/setup-checklist/dismiss').catch(() => ({ ...c, show: false }))); };
  return (
    <section class="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-4 md:p-5" data-setup-checklist aria-label="Get set up">
      <div class="flex flex-wrap items-center gap-x-3 gap-y-1">
        <h2 class="text-[15px] font-semibold text-[var(--color-text)]">Get set up</h2>
        <span class="text-[12.5px] text-[var(--color-text-muted)]" data-setup-progress>{n} of {c.steps.length} done</span>
        <button type="button" onClick={hide} class="ml-auto text-[var(--color-text-muted)] hover:text-[var(--color-text)]"><span class="text-[12.5px] underline">Hide this</span></button>
      </div>
      <div class="h-1.5 rounded-full bg-[var(--color-elevated)] overflow-hidden mt-2.5">
        <div class="h-full rounded-full bg-[var(--color-accent)]" style={{ width: `${(n / c.steps.length) * 100}%` }} />
      </div>
      <ol class="mt-3 grid gap-1.5 sm:grid-cols-2 lg:grid-cols-4">
        {c.steps.map((s) => (
          <li key={s.id}>
            <Link href={s.href} data-setup-step={s.id} data-done={s.done ? '1' : '0'}
              class={'flex items-center gap-2 rounded-lg px-2.5 py-2 text-[13px] border ' + (s === next ? 'border-[var(--color-accent)] bg-[var(--color-accent-soft)]' : 'border-transparent hover:bg-[var(--color-elevated)]')}>
              <span class={'w-5 h-5 shrink-0 rounded-full grid place-items-center border ' + (s.done ? 'bg-[var(--color-accent)] border-[var(--color-accent)]' : 'border-[var(--color-border)] bg-[var(--color-card)]')}>
                {s.done && <Check size={12} color="#ffffff" />}
              </span>
              <span class={'flex-1 min-w-0 ' + (s.done ? 'text-[var(--color-text-muted)] line-through' : 'text-[var(--color-text)]')}>{s.label}</span>
              {!s.done && <ChevronRight size={14} class="shrink-0 text-[var(--color-text-faint)]" />}
              <span class="sr-only">{s.done ? '(done)' : '(to do)'}</span>
            </Link>
          </li>
        ))}
      </ol>
    </section>
  );
}
